import React, { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { mdiChevronLeft, mdiStar, mdiClockTimeFourOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { Button, Col, FormFeedback, Input, Label, Form } from 'reactstrap';
import {
  applyOvertime,
  fetchOvertimeByTransactionId,
  fetchOvertimeDetails,
} from '../../../store/apis/overTimeService';
import {
  initialValues,
  validationSchema,
  populateFormValues,
} from './constants';
import {
  ErrorModalWrapper,
  ErrorTable,
  FileUploadDropzone,
  TimePicker,
  DatePicker,
} from '../../../components/Common';
import { DateUtil, StringUtil } from '../../../utils';
import { downloadBlobType } from '../../../store/apis/commonService';
import { getCompanyCode } from '../../../config/axios.config';
import { companySet1, companySet2 } from '../constants';

function ApplyOverTime() {
  document.title = 'Apply Overtime | Envision - Daikin';

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { CoCd, ID } = Object.fromEntries(query);
  const [errorModal, setErrorModal] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const fileInputRef = useRef(null);
  const formType = ID ? 'view' : 'apply';
  const selectedCoCd = getCompanyCode();

  const toggleErrorModal = () => setErrorModal(!errorModal);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setError,
    clearErrors,
    setValue,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  });

  const { mutate: applyOvertimeMutation, isPending: applyOvertimePending } =
    useMutation({
      mutationFn: payload => applyOvertime(payload),
      onSuccess: data => {
        if (data.flag === 'True') {
          toast.success(
            `OT Request #${data?.transactionNo} Has Been Submitted Successfully`,
            {
              className: 'toast-success',
            },
          );
          reset();
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else if (data.flag === 'False') {
          data.isTable = false;
          setErrorModal(true);
          setErrorData(data);
        }
      },
    });

  const { data: overtimeData } = useQuery({
    queryKey: ['overtimeData', ID],
    queryFn: () =>
      fetchOvertimeByTransactionId({
        transNo: ID,
        companyCode: CoCd,
      }),
    enabled: formType === 'view',
  });

  const downloadFileMutation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const { mutate: overtimeDetailsMutate } = useMutation({
    mutationFn: payload => fetchOvertimeDetails(payload),
    onSuccess: (data, params) => {
      if (data) {
        if (data.length === 0) {
          toast.error(
            `Timing information not available for ${params?.date} in access control device`,
            {
              className: 'toast-error',
            },
          );
        }
        if (data.length > 0) {
          if (data[0]?.TimeIn === null || data[0]?.TimeOut === null) {
            toast.error(
              `Timing information not available for ${params?.date} in access control device`,
              {
                className: 'toast-error',
              },
            );
          } else {
            const fromTime = DateUtil.convertTimeToHTMLTime(data[0]?.TimeIn);
            const toTime = DateUtil.convertTimeToHTMLTime(data[0]?.TimeOut);
            setValue('fromTime', fromTime);
            setValue('toTime', toTime);
            setValue('otHrs', data[0]?.OTHrs);
          }
        }
      }
    },
  });

  const calculateOvertime = (fromTime, toTime) => {
    if (!fromTime || !toTime) {
      return '0.00';
    }
    const from = new Date(`1970-01-01T${fromTime}:00`);
    const to = new Date(`1970-01-01T${toTime}:00`);
    const diff = (to - from) / (1000 * 60 * 60);
    return diff.toFixed(2);
  };

  const onSubmit = async values => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'file' && value && value.length > 0) {
        value.forEach(file => formData.append(key, file));
      } else {
        formData.append(key, value || '');
      }
    });
    if (values?.file && values?.file.length > 0) {
      formData.append('file', values?.file[0]);
    }
    const overtimeDateHTML = DateUtil.formatDateObjToHtmlDate(values.otDate);

    const overtimeDate = `${overtimeDateHTML} 00:00:00`;
    const otFromDate = `${overtimeDateHTML} ${values.fromTime}:00`;
    const otToDate = `${overtimeDateHTML} ${values.toTime}:00`;

    // Additional Values
    formData.set('otDate', overtimeDate);
    formData.set('otFromDate', otFromDate);
    formData.set('otToDate', otToDate);

    applyOvertimeMutation(formData);
  };

  const watchValues = watch();

  useEffect(() => {
    if (formType === 'view' && overtimeData && overtimeData?.length > 0) {
      const editFormValues = populateFormValues(overtimeData?.[0]);
      reset(editFormValues);

      const raw = editFormValues.attachmentPath;
      if (!raw || raw === '-' || raw.trim() === '') return;

      let attachmentArray = [];
      attachmentArray = StringUtil.checkStringOrJsonArray(raw);
      (async () => {
        try {
          const files = await Promise.all(
            attachmentArray.map(async fileName => {
              const response = await downloadFileMutation.mutateAsync({
                fileName,
                type: 'OverTime',
              });
              return new File([response], fileName, { type: response.type });
            }),
          );
          setValue('file', files);
          if (fileInputRef.current) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(files);
            fileInputRef.current.files = dataTransfer.files;
          }
        } catch (err) {
          toast.error('Error downloading attachments. Please try again.', {
            className: 'toast-error',
          });
        }
      })();
    }
  }, [formType, overtimeData]);

  const handleAutoFill = async () => {
    if (!watchValues?.otCategory === 'In Office') {
      return;
    }

    if (!watchValues?.otDate) {
      toast.error('Please select overtime date', {
        className: 'toast-error',
      });
      return;
    }

    overtimeDetailsMutate({
      date: watchValues?.otDate,
    });
  };

  useEffect(() => {
    // Calculate hours whenever fromTime or toTime changes
    const { fromTime, toTime } = watchValues;
    if (fromTime && toTime) {
      const hrs = calculateOvertime(fromTime, toTime);
      setValue('hrs', hrs);
    } else {
      setValue('hrs', 0);
      setValue('otHrs', '');
    }
  }, [watchValues.fromTime, watchValues.toTime, setValue]);

  useEffect(() => {
    if (errorData && !errorData?.isTable) {
      toast.error(errorData?.message || 'An error occurred', {
        className: 'toast-error',
      });
    }
  }, [errorData]);

  return (
    <div className="page-content px-0 apply-forms">
      <div className="d-flex flex-row p-0">
        <div className="col-7 p-0 h-100">
          <div className="d-flex flex-row align-items-center justify-content-between leave-layout-border-bottom">
            {formType !== 'view' ? (
              <div className="ps-3 py-3 d-flex align-items-center fs-5 fw-semibold apply-header">
                Apply Overtime
              </div>
            ) : (
              <div className="ps-1 py-3 d-flex flex-row align-items-center gap-2 apply-header">
                <button
                  type="button"
                  className="btn btn-link p-0 back-btn"
                  onClick={() => window.history.back()}
                >
                  <Icon path={mdiChevronLeft} size={1.1} />
                  <span className="back fs-5">Back</span>
                </button>
                <div
                  style={{
                    width: '1px',
                    borderLeft: '1px solid lightgrey',
                    height: '20px',
                  }}
                />
                <div className="d-flex flex-row gap-1">
                  <span className="request-id-span fs-5">Request ID</span>
                  <span className="request-id-span-value fs-5">{ID}</span>
                </div>
              </div>
            )}{' '}
          </div>
          <Form
            className="d-flex flex-column gap-2 p-3"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Col>
              <div>
                <Label className="my-1">
                  <span>Overtime Category</span>
                  <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="otCategory"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className={`form-select external-filter-input ${errors.otCategory && 'is-invalid'}`}
                      disabled={formType === 'view'}
                      title="Select the category of overtime from the available dropdown list."
                    >
                      <option value="">Select OT Category</option>
                      <option value="In Office">In Office</option>
                      <option value="Out of Office">Out of Office</option>
                    </Input>
                  )}
                />

                {errors.otCategory && (
                  <FormFeedback>{errors.otCategory.message}</FormFeedback>
                )}
              </div>
            </Col>
            <Col>
              <div>
                <Label className="my-1">
                  <span>Reason</span>
                  <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="reason"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      rows="3"
                      placeholder="Enter Reason"
                      className={`form-control external-filter-input ${errors.reason && 'is-invalid'}`}
                      disabled={formType === 'view'}
                      title="Enter the reason for requesting overtime."
                    />
                  )}
                />

                {errors.reason && (
                  <FormFeedback>{errors.reason.message}</FormFeedback>
                )}
              </div>
            </Col>
            <Col>
              <div>
                <Label className="my-1">
                  <span>Date</span>
                  <span className="text-danger">*</span>
                </Label>
                <div className="d-flex flex-row align-items-start gap-2">
                  <div className="flex-grow-1">
                    <Controller
                      name="otDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          disabled={
                            formType === 'view' ||
                            (watchValues.fromTime &&
                              watchValues.toTime &&
                              watchValues.otCategory === 'In Office')
                          }
                          className={`external-filter-input ${errors.otDate ? 'is-invalid' : ''}`}
                          error={!!errors.otDate}
                          title="Select the date on which the overtime work was done."
                        />
                      )}
                    />
                    {errors.otDate && (
                      <div className="invalid-feedback d-block">
                        {errors.otDate.message}
                      </div>
                    )}
                  </div>
                  <Button
                    className=" external-filter-input d-flex flex-row align-items-center gap-1"
                    style={{
                      background: 'Transparent',
                      border: '1px solid #017EBD',
                      color: '#017EBD',
                      cursor:
                        watchValues?.otCategory !== 'In Office' && 'pointer',
                    }}
                    disabled={
                      watchValues?.otCategory !== 'In Office' ||
                      formType === 'view'
                    }
                    onClick={handleAutoFill}
                  >
                    <Icon path={mdiClockTimeFourOutline} size={0.7} />
                    <span
                      title="Automatically fetch the 'From Time' and 'To Time' for the selected Overtime date from existing biometric data, if available.
Applicable for 'In Office' Overtime category."
                      style={{
                        textWrap: 'nowrap',
                      }}
                    >
                      Autofill
                    </span>
                  </Button>
                </div>
              </div>
            </Col>
            <div className="d-flex flex-row gap-2">
              <Col>
                <div>
                  <Label className="my-1">
                    <span>From Time</span>
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="fromTime"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        {...field}
                        interval={15}
                        disabled={
                          formType === 'view' ||
                          watchValues?.otCategory === 'In Office'
                        }
                        className={`mpi-time-picker external-filter-input ${errors.fromTime ? 'is-invalid' : ''} time-picker-pointer`}
                        error={!!errors.fromTime}
                        title="Enter the start time of the overtime worked."
                      />
                    )}
                  />
                  {errors.fromTime && (
                    <div className="invalid-feedback d-block">
                      {errors.fromTime.message}
                    </div>
                  )}
                </div>
              </Col>
              <Col>
                <div>
                  <Label className="my-1">
                    <span>To Time</span>
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="toTime"
                    control={control}
                    render={({ field }) => (
                      <TimePicker
                        {...field}
                        interval={15}
                        disabled={
                          formType === 'view' ||
                          watchValues?.otCategory === 'In Office'
                        }
                        className={`mpi-time-picker external-filter-input ${errors.toTime ? 'is-invalid' : ''} time-picker-pointer`}
                        error={!!errors.toTime}
                        title="Enter the end time of the overtime worked."
                      />
                    )}
                  />
                  {errors.toTime && (
                    <div className="invalid-feedback d-block">
                      {errors.toTime.message}
                    </div>
                  )}
                </div>
              </Col>
            </div>
            <Col>
              <div>
                <Label className="my-1">Hours</Label>
                <Controller
                  name="hrs"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      value={calculateOvertime(
                        watchValues.fromTime,
                        watchValues.toTime,
                      )}
                      className={`form-control external-filter-input ${errors.hrs && 'is-invalid'}`}
                      placeholder="Enter Hours"
                      readOnly
                      disabled
                      title="Total number of hours worked based on 'From Time' and 'To Time'."
                    />
                  )}
                />
              </div>
            </Col>
            <Col>
              <div>
                <Label className="my-1">Overtime Hours</Label>
                <Controller
                  name="otHrs"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      className={`form-control external-filter-input ${errors.otHrs && 'is-invalid'}`}
                      placeholder="Enter Hours"
                      disabled={
                        formType === 'view' ||
                        !watchValues.hrs ||
                        watchValues?.otCategory === 'In Office'
                      }
                      title="Total number of hours worked based on 'From Time' and 'To Time'."
                    />
                  )}
                />
                {errors.otHrs && (
                  <div className="invalid-feedback d-block">
                    {errors.otHrs.message}
                  </div>
                )}
              </div>
            </Col>
            <Col>
              <div>
                <Label className="my-1">
                  <span>Attach Evidence for Prior Approval</span>
                  <span className="text-danger">*</span>
                </Label>
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <FileUploadDropzone
                      error={errors.file}
                      onDrop={async acceptedFiles => {
                        try {
                          await validationSchema.fields.file.validate(
                            acceptedFiles,
                          );
                          clearErrors('files');
                          setValue('file', acceptedFiles, {
                            shouldValidate: true,
                          });
                        } catch (err) {
                          setError('file', {
                            type: 'manual',
                            message: err.message,
                          });
                        }
                      }}
                      selectedFiles={field.value}
                      className={errors.file ? 'is-invalid' : ''}
                      handleDeleteFile={file => {
                        field.onChange(
                          field.value.filter(f => f.name !== file.name),
                        );
                      }}
                      disabled={formType === 'view'}
                      showDeleteButton={formType !== 'view'}
                      tooltipTitle="Upload any prior overtime approval documents"
                      showDownloadButton={formType === 'view'}
                      multiple
                    />
                  )}
                />
                {errors.file && (
                  <FormFeedback>{errors.file.message}</FormFeedback>
                )}
              </div>
            </Col>
            {formType !== 'view' && (
              <div className="d-flex flex-row align-items-center justify-content-center gap-2 p-3">
                <Button
                  className="custom-light-btn"
                  style={{
                    width: '25%',
                  }}
                  onClick={() => reset(initialValues)}
                  disabled={formType === 'view'}
                >
                  Clear
                </Button>
                <Button
                  className="custom-btn"
                  style={{
                    width: '25%',
                  }}
                  disabled={
                    !isValid || formType === 'view' || applyOvertimePending
                  }
                  type="submit"
                >
                  <span>
                    {applyOvertimePending ? 'Submitting...' : 'Submit'}
                  </span>
                </Button>
              </div>
            )}
          </Form>
        </div>
        <div
          className="col-5 leave-layout-border-left"
          style={{ color: 'black' }}
        >
          <div className="tnc-wrapper p-3">
            <b className="tnc-title mb-1">Terms & Conditions</b>
            <ul>
              <li>
                Overtime applicable only for special cases, preapproval by
                Direct Manager and Biometric registration required except for
                technicians/drivers.
              </li>
              <li>No overtime availed for training.</li>
              <li>
                Overtime not applicable for Sales and for staff on functional
                classification 4D and above.
              </li>
              {companySet1.includes(selectedCoCd) && (
                <li>
                  Overtime not applicable on Weekend/Public Holiday except for
                  technicians/drivers.
                </li>
              )}
              {companySet2.includes(selectedCoCd) && (
                <li>
                  Technicians&apos; overtime is not applicable through envision.
                </li>
              )}
            </ul>
            {companySet1.includes(selectedCoCd) && (
              <div className="d-flex flex-row align-items-center gap-1">
                <Icon path={mdiStar} size={1} color="#DC6803" />
                <span>Please</span>
                <a
                  href="https://denv.sharepoint.com/sites/eu-intra-dame-hr-en/SitePages/Overtime.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="clickable"
                >
                  click here
                </a>
                <span>to view the leave policy in detail.</span>
              </div>
            )}
          </div>
        </div>
        {/* </div> */}
      </div>
      {errorData && errorData?.isTable && (
        <ErrorModalWrapper
          isModalOpen={errorModal}
          toggle={toggleErrorModal}
          errorMessage={errorData?.message}
          onButtonClick={toggleErrorModal}
          size="xl"
        >
          {errorData?.isTable && (
            <ErrorTable errorData={errorData?.errorData} />
          )}
        </ErrorModalWrapper>
      )}
    </div>
  );
}

export default ApplyOverTime;
